import type { KeyboardEventHandler } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { kebabCase } from '@makemydeal/dr-common-utils';
import { convertToNumber, formatAprRate, formatMoneyFactor, getConvertedRateText } from '../../utils/formatUtils';
import { APR, APR_PERCENTAGE, BUY_RATE, GENERIC_LAST_FIELD_CHANGED_ERROR, MONEY_FACTOR, MONEY_FACTOR_TYPE } from '../../constants';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import { dealLockHooks } from '../../hooks';
import { useFieldOverrideIndicator } from '../../hooks/useFieldOverrideIndicator';
import { useUserTypedGuard } from '../../hooks/useUserTypedGuard';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import {
    StyledRateConversionHelperText,
    StyledRateFieldTextWithToggleGroupText,
    StyledTextWithToggleGroupContainer,
    StyledTextWithToggleGroupToggleCompact
} from './StyledTextWithToggleGroup.styles';
import RateTypeToggleGroup from './RateTypeToggleGroup.interstate';
import BuyRateInput from './BuyRateInput.interstate';
import FieldOverrideIndicator from './FieldOverrideIndicator.interstate';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { useHeldSelector } from '../../hooks/useHeldSelector';

const BUY_RATE_OVERRIDE_INDICATOR_TEST_ID = 'buy-rate-override-indicator';

const BuyRateField = ({ tabName }: { tabName?: DealTabs }) => {
    const isSummaryTab = tabName === DealTabs.SUMMARY;
    const isLenderTab = tabName === DealTabs.LENDER;
    const dispatch = useDispatch();

    const isUserProgramQuotes = useSelector(offerReduxSelectors.getIsUserProgramQuotes);
    const value = useHeldSelector(offerSelectors.getBuyRateOverrideWithFallback);
    const rateType = useSelector(offerSelectors.getRateTypeOverrideFromOfferTypeOrSelectedTerm);
    const currentTermMonths = useSelector(offerSelectors.getSelectedTermMonths);
    const failed = useSelector(deskingSelectors.wasTheLastFailure(offerActionTypes.UPDATED_BUY_RATE_OVERRIDE));
    const selectedTerm = useSelector(offerReduxSelectors.getSelectedTerm);
    const currentOfferType: OfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const months = useSelector(offerReduxSelectors.getSelectedTermMonths);
    const isAprRateType = rateType === APR;
    const rateTypePrefix = isAprRateType ? APR_PERCENTAGE : MONEY_FACTOR;
    const isPaymentGridEnabled = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridToggle(state, currentOfferType)
    );
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isImprovedAutoSelectEnabled = useSelector(featureToggleSelectors.isImprovedAutoSelectEnabled);
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const isBuyRatePqFirstEnabled = useSelector(featureToggleSelectors.isBuyRatePqFirstEnabled);
    const isLocked = dealLockHooks.useIsDealLocked();
    const isDisabled = isImprovedAutoSelectEnabled ? isLocked : isLocked || !isUserProgramQuotes;
    const {
        enabled: overrideIndicatorEnabled,
        show: showOverrideIndicator,
        message: overrideMessage,
        recordManualOverride
    } = useFieldOverrideIndicator('buyRate');
    const { markTyped, recordIfUserEdited } = useUserTypedGuard(recordManualOverride);
    const failedMessage = useMemo(() => (failed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined), [failed]);

    // determine formatter by rate type
    const formatter = useMemo(() => {
        if (rateType === MONEY_FACTOR_TYPE) return formatMoneyFactor;
        return formatAprRate;
    }, [rateType]);

    const rateTypeLabel = useMemo(() => {
        if (rateType === MONEY_FACTOR_TYPE) return MONEY_FACTOR;
        return APR;
    }, [rateType]);

    // grab the current, foratted value
    const formattedValue = useMemo(() => formatter(value), [value, formatter, rateType]);

    // setup state for the actual input; default with currently formatted state value
    const [inputText, setInputText] = useState(formattedValue);

    // calculate converted rate for PQ display
    const convertedRateText = useMemo(() => getConvertedRateText(rateType, value, true), [value, rateType]);

    // action creator to also consider selected term months
    const actionCreator = useCallback(
        (newValue: number) => {
            if (typeof currentTermMonths !== 'number') return;

            return offerReduxActionCreators.updateBuyRateOverride(newValue, currentTermMonths, {
                typedByUser: isBuyRatePqFirstEnabled
            });
        },
        [currentTermMonths, isBuyRatePqFirstEnabled]
    );

    const handleInputChanged = (e: any) => {
        const newValue = convertToNumber(e.target.value);
        const prevValue = value;

        if (e.target.value === inputText || isNaN(newValue)) return;

        markTyped(e);

        dispatch(offerActionCreators.updatePaymentCallPendingState(prevValue !== newValue));

        setInputText(e.target.value);
    };

    const handleInputBlur = (_e: any) => {
        const formattedInputText = formatter(convertToNumber(inputText));
        if (formattedInputText !== inputText) setInputText(formattedInputText);

        if (value !== convertToNumber(inputText)) {
            dispatch(actionCreator(convertToNumber(inputText)));
            recordIfUserEdited();
        }
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') {
            if (value !== convertToNumber(inputText)) {
                dispatch(actionCreator(convertToNumber(inputText)));
                recordIfUserEdited();
            }

            e.preventDefault();
        }
    };

    // NOTE: upstream change; i.e. term changed in redux causing new override to be set
    // TODO: future integration test here.
    // istanbul ignore next
    useEffect(() => {
        if (inputText === formattedValue) return undefined;
        setInputText(formattedValue);
        return undefined;
    }, [formattedValue]);

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled) {
            const isPaymentError: boolean = failed;
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.LENDER, kebabCase(BUY_RATE), failed, isPaymentError));
        }
    }, [failed, dispatch]);

    const handleTogglesClick = () => {
        const rateType = !isAprRateType ? APR : MONEY_FACTOR_TYPE;
        dispatch(baseDealActionCreators.toggleRateType(rateType, selectedTerm.id, currentOfferType, months));
    };

    const handleOfferType = () => {
        if (offerType === FINANCE) dispatch(interactionAnalyticsActionCreators.financeBuyRateTextInput());
        else if (offerType === LEASE) dispatch(interactionAnalyticsActionCreators.leaseBuyRateTextInput());
    };

    return (
        <>
            {currentOfferType === 'lease' ? (
                <StyledTextWithToggleGroupContainer>
                    <StyledRateFieldTextWithToggleGroupText>
                        <FieldOverrideIndicator
                            enabled={overrideIndicatorEnabled}
                            show={showOverrideIndicator}
                            message={overrideMessage}
                            testId={BUY_RATE_OVERRIDE_INDICATOR_TEST_ID}
                            reserveSpace={!isLenderTab}
                            overlayIcon={isLenderTab}
                            showTooltip={!isExplicitLenderP1Enabled}
                        >
                            <BuyRateInput
                                size="sm"
                                label={`${BUY_RATE}`}
                                name={kebabCase(BUY_RATE)}
                                id={kebabCase(BUY_RATE)}
                                inputPrefix={rateTypePrefix}
                                onChange={handleInputChanged}
                                onBlur={(e) => {
                                    handleInputBlur(e);
                                    handleOfferType();
                                }}
                                onKeyDown={handleKeyDown}
                                errorMessage={failedMessage}
                                hasError={failed}
                                placeholder={formatter(0)}
                                value={inputText}
                                disabled={isDisabled}
                                dataTestId={kebabCase(BUY_RATE)}
                            />
                            {!isUserProgramQuotes && !isPaymentGridEnabled && !isSummaryTab && (
                                <StyledRateConversionHelperText data-testid="buy-rate-conversion-helper">
                                    {convertedRateText}
                                </StyledRateConversionHelperText>
                            )}
                        </FieldOverrideIndicator>
                    </StyledRateFieldTextWithToggleGroupText>
                    {!isPaymentGridEnabled && isUserProgramQuotes && (
                        <StyledTextWithToggleGroupToggleCompact>
                            <RateTypeToggleGroup
                                inputField="buy-rate"
                                isAPRRateType={isAprRateType}
                                handleOnClick={handleTogglesClick}
                                disabled={isLocked}
                            />
                        </StyledTextWithToggleGroupToggleCompact>
                    )}
                </StyledTextWithToggleGroupContainer>
            ) : (
                <FieldOverrideIndicator
                    enabled={overrideIndicatorEnabled}
                    show={showOverrideIndicator}
                    message={overrideMessage}
                    testId={BUY_RATE_OVERRIDE_INDICATOR_TEST_ID}
                    reserveSpace={!isLenderTab}
                    overlayIcon={isLenderTab}
                    showTooltip={!isExplicitLenderP1Enabled}
                >
                    <BuyRateInput
                        size="sm"
                        label={`${BUY_RATE} (${rateTypeLabel})`}
                        name={kebabCase(BUY_RATE)}
                        id={kebabCase(BUY_RATE)}
                        onChange={handleInputChanged}
                        onBlur={(e) => {
                            handleInputBlur(e);
                            handleOfferType();
                        }}
                        onKeyDown={handleKeyDown}
                        errorMessage={failedMessage}
                        hasError={failed}
                        placeholder={formatter(0)}
                        value={inputText}
                        disabled={isDisabled}
                        dataTestId={kebabCase(BUY_RATE)}
                    />
                </FieldOverrideIndicator>
            )}
        </>
    );
};

export default BuyRateField;
